import { Card, CardContent, CardHeader, CardTitle, cn } from "@devhop/ui";
import { BarChart3Icon } from "lucide-react";
import type { ExpensesSummaryDTO } from "../api/client";
import { EXPENSE_STATUS_LABELS, formatExpenseAmount } from "../lib/expense-format";

type ExpenseOverviewStatusBreakdownProps = {
  summary: ExpensesSummaryDTO | undefined;
  isLoading: boolean;
  onStatusClick?: (status: string) => void;
  compact?: boolean;
};

type BreakdownRow = {
  status: string;
  count: number;
  amount?: number;
};

function buildRows(summary: ExpensesSummaryDTO | undefined): BreakdownRow[] {
  if (!summary) return [];

  return [
    { status: "draft", count: summary.draftCount, amount: summary.draftAmount },
    {
      status: "submitted",
      count: summary.submittedCount,
      amount: summary.submittedAmount,
    },
    {
      status: "approved",
      count: summary.approvedCount,
      amount: summary.approvedAmount,
    },
    { status: "rejected", count: summary.rejectedCount },
    {
      status: "paid",
      count: summary.paidCount + summary.closedCount,
      amount: summary.paidAmount,
    },
    { status: "cancelled", count: summary.cancelledCount },
    { status: "voided", count: summary.voidedCount },
  ].filter((row) => row.count > 0 || row.status === "draft");
}

export function ExpenseOverviewStatusBreakdown({
  summary,
  isLoading,
  onStatusClick,
  compact = false,
}: ExpenseOverviewStatusBreakdownProps) {
  const rows = buildRows(summary);
  const cellPad = compact ? "px-4 py-2.5" : "px-6 py-3";
  const headerPad = compact ? "px-4 py-3" : "px-6 py-4";
  const emptyPad = compact ? "px-4 py-6" : "px-6 py-8";

  return (
    <Card className="gap-0 py-0">
      <CardHeader className={cn("border-b", headerPad)}>
        <CardTitle
          className={cn(
            "flex items-center gap-2",
            compact ? "text-sm" : "text-base",
          )}
        >
          <BarChart3Icon className="size-4 text-muted-foreground" />
          ສະຫຼຸບຕາມສະຖານະ
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <p className={cn(emptyPad, "text-muted-foreground text-sm")}>
            ກຳລັງໂຫຼດ...
          </p>
        ) : rows.length === 0 ? (
          <p className={cn(emptyPad, "text-muted-foreground text-sm")}>
            ຍັງບໍ່ມີລາຍການ
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className={cn("w-full", compact ? "text-xs" : "text-sm")}>
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className={cn(cellPad, "font-medium")}>ສະຖານະ</th>
                  <th className={cn(cellPad, "text-right font-medium")}>
                    ຈຳນວນ
                  </th>
                  <th className={cn(cellPad, "text-right font-medium")}>
                    ຍອດ (ກີບ)
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const clickable = !!onStatusClick;
                  return (
                    <tr
                      key={row.status}
                      className={cn(
                        "border-b last:border-0",
                        clickable && "cursor-pointer hover:bg-muted/30",
                      )}
                      onClick={
                        clickable
                          ? () => onStatusClick(row.status)
                          : undefined
                      }
                    >
                      <td className={cellPad}>
                        {EXPENSE_STATUS_LABELS[row.status] ?? row.status}
                      </td>
                      <td className={cn(cellPad, "text-right tabular-nums")}>
                        {row.count.toLocaleString("lo-LA")}
                      </td>
                      <td className={cn(cellPad, "text-right tabular-nums")}>
                        {row.amount != null
                          ? formatExpenseAmount(row.amount)
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
